/**
 * Reusable client-side table sort primitive (CHP-417).
 *
 * One sort engine shared by every data ledger so the interaction is identical
 * everywhere: clicking a column header sorts ascending, clicking it again
 * toggles to descending, and a third click resets to the table's default order.
 *
 * The primitive is deliberately tiny and framework-agnostic — it knows nothing
 * about React. `useTableSort` wires the cycle into component state and
 * `applySort` produces the ordered rows; both are thin wrappers around the two
 * pure functions here so the behaviour stays unit-testable in isolation.
 */

/** Tri-state direction. `null` is the default (unsorted) state — a third click
 * returns here, restoring whatever order the caller supplied. */
export type SortDirection = "asc" | "desc" | null;

export interface SortState<ColumnKey extends string> {
  /** The column currently driving the sort, or `null` when unsorted. */
  column: ColumnKey | null;
  direction: SortDirection;
}

/** The default (unsorted) state — nothing is sorting, rows keep their input order. */
export const NO_SORT: SortState<never> = { column: null, direction: null };

/**
 * Advance the tri-state sort cycle for a header click.
 *
 * Clicking a column that is not the active one starts a fresh ascending sort.
 * Clicking the active column walks asc → desc → reset (back to the default,
 * unsorted state). This is the canonical CHP-417 three-click behaviour.
 */
export function nextSortState<ColumnKey extends string>(
  current: SortState<ColumnKey>,
  column: ColumnKey,
): SortState<ColumnKey> {
  if (current.column !== column) {
    // First click on a new column → ascending.
    return { column, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { column, direction: "desc" };
  }
  // Was descending (or somehow null on the active column) → reset to default.
  return { column: null, direction: null };
}

/**
 * Comparator for one cell value. Numbers and dates compare numerically,
 * strings compare case-insensitively via `localeCompare` (so "apple" < "Banana"),
 * and `null`/`undefined`/empty always sort last regardless of direction — a
 * blank cell is "no value", never a value that beats a real one.
 *
 * Date strings are detected by the caller choosing a `kind`; we don't sniff,
 * because an ISO date string would otherwise sort lexically (which happens to
 * be correct for ISO, but is fragile for other formats).
 */
export type SortValue = string | number | Date | null | undefined;

function isBlank(value: SortValue): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

/**
 * Compare two extracted cell values for ascending order. Blanks always sink to
 * the bottom (returned as if "greater") so they cluster at the end in both
 * directions once `applySort` flips the sign for descending.
 */
export function compareValues(a: SortValue, b: SortValue): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  if (a instanceof Date || b instanceof Date) {
    const at = a instanceof Date ? a.getTime() : Number(a);
    const bt = b instanceof Date ? b.getTime() : Number(b);
    return at - bt;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  // String (or mixed) — case-insensitive, locale-aware. The "Owner"/"Attendees"
  // individual-name columns (CHP-417) flow through here: they sort on the
  // primary name string, so "Alice" precedes "bob" precedes "Charlie".
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

/**
 * Order a copy of `rows` per `state`, using `getValue` to extract the cell value
 * for the active column. Returns the input array unchanged (same reference) when
 * unsorted, so callers can cheaply fall back to their own default order.
 *
 * Stable: blanks always trail, and `Array.prototype.sort` is stable in every
 * supported engine, so rows that tie keep their input order.
 */
export function applySort<Row, ColumnKey extends string>(
  rows: readonly Row[],
  state: SortState<ColumnKey>,
  getValue: (row: Row, column: ColumnKey) => SortValue,
): Row[] {
  if (state.column == null || state.direction == null) {
    return rows as Row[];
  }
  const { column, direction } = state;
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const cmp = compareValues(getValue(a, column), getValue(b, column));
    // Blanks must trail in BOTH directions, so don't flip a comparison that is
    // purely "one side is blank" — detect it and keep blanks last.
    if (cmp !== 0) {
      const aBlank = isBlank(getValue(a, column));
      const bBlank = isBlank(getValue(b, column));
      if (aBlank !== bBlank) return cmp; // blank-vs-real: keep blank last
      return cmp * sign;
    }
    return 0;
  });
}
